import { ref } from 'vue';

type RuntimeConfigMap = Record<string, string | undefined>;

declare global {
  interface Window {
    __KB_RUNTIME__?: RuntimeConfigMap;
  }
}

function runtimeConfig(): RuntimeConfigMap {
  if (typeof window === 'undefined') return {};
  return window.__KB_RUNTIME__ || {};
}

// Tenant slug = first label of the per-tenant host `{slug}.{LUMI_DOMAIN}`.
// The workspace is now the bare-host root app, so its host IS `{slug}.{domain}`.
export function tenantSlugFromWorkspaceHost() {
  if (typeof window === 'undefined') return '';
  const host = String(window.location.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return '';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return '';
  const labels = host.split('.');
  if (labels.length < 3) return '';
  const first = labels[0];
  if (first === 'www' || first === 'sysadmin') return '';
  return /^[a-z0-9-]+$/.test(first) ? first : '';
}

function replaceTenantPlaceholder(value: string) {
  if (!value.includes('{tenant}')) return value;
  const tenantSlug = tenantSlugFromWorkspaceHost();
  return tenantSlug ? value.split('{tenant}').join(tenantSlug) : value;
}

export function runtimeValue(key: string, fallback = ''): string {
  const value = String(runtimeConfig()[key] || '').trim();
  return replaceTenantPlaceholder(value || fallback);
}

export function runtimeMasterApiBaseUrl(): string {
  return runtimeValue('MASTER_API_BASE_URL', '/api/master');
}

export function runtimeBackofficeUrl(): string {
  return runtimeValue('BACKOFFICE_URL', '');
}

export function runtimeTimeTrackingUrl(): string {
  return runtimeValue('TIME_TRACKING_URL', '');
}

export function runtimeApprenticeUrl(): string {
  return runtimeValue('APPRENTICE_URL', '');
}

export function runtimeUserApiBaseUrl(): string {
  const configured = runtimeValue('USER_API_BASE_URL', '');
  if (configured) return configured;
  return '/api/user';
}

// Tenant-scoped support ticket API (X-Tenant-Slug header set per-call). Falls
// back to the same-origin `/api/support` dev/ingress path.
export function runtimeSupportApiBaseUrl(): string {
  return runtimeValue('SUPPORT_API_BASE_URL', '/api/support');
}

export function runtimePeopleUrl(): string {
  return runtimeValue('PEOPLE_URL', '');
}

export function runtimeLumiDomain(): string {
  return runtimeValue('LUMI_DOMAIN', '');
}

export function runtimeLumiProtocol(): string {
  return runtimeValue('LUMI_PROTOCOL', 'https');
}

export function runtimeNotificationApiBaseUrl(): string {
  const configured = runtimeValue('NOTIFICATION_API_BASE_URL', '');
  return configured || '/api/notification';
}

export function runtimeAssistantApiBaseUrl(): string {
  const configured = runtimeValue('ASSISTANT_API_BASE_URL', '');
  return configured || '/api/assistant';
}

// Module API base URLs for the homebase `user-dashboard` fan-out. On the shared
// per-tenant host every module API is same-origin under a path prefix
// (ADR 0013): ats `/api`, time `/api/time`, apprentice `/api/apprentice`,
// people `/api/people`. No cross-origin CORS is involved.
export function runtimeAtsApiBaseUrl(): string {
  return runtimeValue('ATS_API_BASE_URL', '/api');
}

export function runtimeTimeApiBaseUrl(): string {
  return runtimeValue('TIME_API_BASE_URL', '/api/time');
}

export function runtimeApprenticeApiBaseUrl(): string {
  return runtimeValue('APPRENTICE_API_BASE_URL', '/api/apprentice');
}

export function runtimePeopleApiBaseUrl(): string {
  return runtimeValue('PEOPLE_API_BASE_URL', '/api/people');
}

// The workspace IS the current origin (it is the per-tenant host root app). Used
// as the module switcher's "back to home" target and the notification popover's
// in-app deep-link base.
export function runtimeWorkspaceUrl(): string {
  const configured = runtimeValue('WORKSPACE_URL', '');
  if (configured) return configured.replace(/\/+$/, '');
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// Bumped whenever the stored tenant scope is written through `setStoredTenantSlug`.
// `localStorage` is not reactive, so a computed that resolved the slug through
// `currentTenantSlug()` never re-evaluated when the tenant store scoped the workspace
// on first login: WorkspaceView's fan-out gate stayed false and the recruiting panel
// rendered empty until a module switch or a reload (issue 195, decision D16 3). The
// counter is the dependency Vue can track; the value itself is still read from storage
// on every call, so a direct write (a test fixture, another tab) is never masked by a
// stale mirror.
const storedTenantSlugVersion = ref(0);

/**
 * Write (or, with an empty slug, drop) the bare/dev host's tenant scope. The ONLY
 * writer of `kb_tenant_slug` — a direct `localStorage.setItem`/`removeItem`
 * elsewhere would leave every reactive reader on the previous value.
 */
export function setStoredTenantSlug(slug: string): void {
  try {
    if (slug) window.localStorage.setItem('kb_tenant_slug', slug);
    else window.localStorage.removeItem('kb_tenant_slug');
  } catch {
    /* ignore */
  }
  storedTenantSlugVersion.value += 1;
}

// The workspace runs on the per-tenant host `{slug}.{domain}` in prod, so the
// slug comes from the host. Locally (localhost) that's empty, so we fall back to
// the slug captured from the SSO handoff query (stored by the router).
//
// REACTIVE on the fallback path: read inside a `computed`, it re-evaluates when
// `setStoredTenantSlug` runs. The host path needs no such thing — a hostname cannot
// change without a page load.
export function currentTenantSlug(): string {
  const fromHost = tenantSlugFromWorkspaceHost();
  if (fromHost) return fromHost;
  if (typeof window === 'undefined') return '';
  void storedTenantSlugVersion.value;
  return String(window.localStorage.getItem('kb_tenant_slug') || '').trim();
}
